const QUEUE_KEY = "trybeup_offline_queue";

// Drop an action after this many failed replay attempts so a sticky 4xx/5xx
// can't loop forever, burn battery, and hide the real error from the user
// and Sentry. 5 attempts × backoff means a transient outage (a few minutes)
// is forgiven but a permanent server-side rejection stops bothering the user.
const MAX_REPLAY_ATTEMPTS = 5;

interface QueuedAction {
  id: string;
  url: string;
  method: string;
  body: Record<string, unknown>;
  timestamp: number;
  attempts?: number; // optional for back-compat with pre-existing queue entries
}

function getQueue(): QueuedAction[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Returns true if the write succeeded. Callers that genuinely depend on
 * persistence (the write-ahead enqueue pattern) MUST check this — if storage
 * is full or disabled (Safari Private Mode), the queue silently can't help.
 */
function saveQueue(queue: QueuedAction[]): boolean {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch {
    return false;
  }
}

/**
 * Atomic helper: read the queue, apply the mutator, write it back. Used
 * everywhere instead of read-mutate-write at the call site so a concurrent
 * flushQueue (which holds a snapshot from start-of-flush) can't silently
 * overwrite a fresh enqueue/dequeue that happened during the flush.
 */
function updateQueue(mutator: (q: QueuedAction[]) => QueuedAction[]): boolean {
  return saveQueue(mutator(getQueue()));
}

/**
 * Enqueue an action and return its id, or `null` if the queue write failed
 * (storage full / disabled). Callers using the write-ahead pattern MUST treat
 * `null` as "we cannot guarantee durability, surface this to the user" —
 * silently proceeding would advertise a write-ahead guarantee the system
 * isn't actually providing.
 */
export function enqueueAction(
  url: string,
  method: string,
  body: Record<string, unknown>
): string | null {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const action: QueuedAction = {
    id,
    url,
    method,
    body,
    timestamp: Date.now(),
    attempts: 0,
  };
  const ok = updateQueue((q) => [...q, action]);
  return ok ? id : null;
}

/**
 * Remove a single queued action by id. Used by the write-ahead pattern: we
 * enqueue the action BEFORE the network call so the data survives a mid-fetch
 * app death, then dequeue on success.
 */
export function dequeueAction(id: string): void {
  updateQueue((q) => q.filter((a) => a.id !== id));
}

export function hasPendingActions(): boolean {
  return getQueue().length > 0;
}

// Synchronous re-entrancy lock. flushQueue snapshots the queue at its start and
// only removes items in the atomic write at its END (after every per-item
// `await`), so two overlapping flushes — the new 60s tick coinciding with an
// `online`/foreground trigger — would each snapshot the SAME pending action and
// POST it concurrently. The habit-log dedupe is a read-then-insert with no DB
// uniqueness, so two concurrent replays of one client_id can both insert (on an
// already-completed day, no HabitCompletion guard fires) → the double-count the
// dedupe exists to prevent. `offlineSync.ts` guards its replay the same way.
// Set BEFORE any await so a same-tick second call can't slip past.
let flushing = false;

export async function flushQueue(): Promise<{ synced: number; failed: number; dropped: number }> {
  if (flushing) return { synced: 0, failed: 0, dropped: 0 };
  flushing = true;
  try {
    return await _flushQueue();
  } finally {
    flushing = false;
  }
}

async function _flushQueue(): Promise<{ synced: number; failed: number; dropped: number }> {
  // We snapshot the queue at start, but write our updates via updateQueue's
  // read-mutate-write helper so any actions enqueued DURING this flush are
  // preserved (and any actions dequeued during the flush stay dequeued).
  const startSnapshot = getQueue();
  if (startSnapshot.length === 0) return { synced: 0, failed: 0, dropped: 0 };

  let synced = 0;
  let failed = 0;
  let dropped = 0;

  // Track which actions we've processed so the final write can leave alone
  // actions that the user enqueued mid-flush.
  const processedIds = new Set<string>();
  const successIds = new Set<string>();
  const droppedIds = new Set<string>();
  // Distinguish "server pushed back (5xx)" from "we couldn't reach the
  // network at all (TypeError)" — only the former should burn an attempt.
  // Otherwise a user offline across 5 app launches eats up the cap on
  // requests the server has never seen.
  const failed5xxIds = new Set<string>();
  const failedNetworkIds = new Set<string>();

  for (const action of startSnapshot) {
    processedIds.add(action.id);
    const attempts = (action.attempts ?? 0) + 1;
    try {
      const { apiFetch } = await import("./api");
      const res = await apiFetch(action.url, {
        method: action.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(action.body),
      });
      if (res.ok) {
        synced++;
        successIds.add(action.id);
      } else if (res.status >= 400 && res.status < 500) {
        // Won't succeed on retry — drop and bump `dropped` so listeners
        // (and downstream UI / Sentry hooks) see a permanent failure.
        failed++;
        dropped++;
        droppedIds.add(action.id);
      } else if (attempts >= MAX_REPLAY_ATTEMPTS) {
        // Server-side sticky 5xx: stop replaying after MAX_REPLAY_ATTEMPTS so
        // we don't loop forever every time the app becomes visible.
        failed++;
        dropped++;
        droppedIds.add(action.id);
      } else {
        failed++;
        failed5xxIds.add(action.id);
      }
    } catch {
      // Network error — keep queued AND don't bump attempts: this is the
      // "we're offline" case (TypeError from fetch), not "server keeps
      // rejecting". Previously the atomic write below bumped these too,
      // so a user offline across 5 app opens would have their queued
      // writes silently dropped before the server ever saw them.
      failed++;
      failedNetworkIds.add(action.id);
    }
  }

  // Atomic write: re-read the queue at write time (mid-flush enqueues
  // included), drop the successful + permanently-failed actions, bump
  // attempts ONLY on 5xx retries (network errors don't count toward the cap).
  updateQueue((current) =>
    current
      .filter((a) => !processedIds.has(a.id) || (!successIds.has(a.id) && !droppedIds.has(a.id)))
      .map((a) => {
        if (failed5xxIds.has(a.id)) {
          return { ...a, attempts: (a.attempts ?? 0) + 1 };
        }
        return a;
      })
  );

  return { synced, failed, dropped };
}

// Allow multiple listeners — previously the `syncListenerAttached` guard
// silently dropped every caller after the first, which meant main.tsx's
// boot-time setup (no callback) blocked MyHabits.tsx's later setup (which
// invalidates the habits cache + toasts the user). Now each call adds an
// independent listener; the window event handlers are still only attached
// once.
let windowListenersAttached = false;
const syncListeners: Array<(result: { synced: number; failed: number; dropped: number }) => void> = [];

/**
 * Register a sync-completion callback. Returns a teardown function that
 * removes the listener — call it from useEffect cleanup so React remounts
 * don't accumulate duplicate callbacks (which would fire duplicate toasts
 * and burst-invalidate the query cache).
 *
 * The window-level "online" / "visibilitychange" listeners are still
 * attached at most once (cheap to leak; nothing useful to remove them
 * with), but the per-call `onSynced` is now disposable.
 */
export function setupOfflineSync(
  onSynced?: (result: { synced: number; failed: number; dropped: number }) => void
): () => void {
  let active = true;
  if (onSynced) syncListeners.push(onSynced);

  const teardown = () => {
    if (!active) return;
    active = false;
    if (onSynced) {
      const i = syncListeners.indexOf(onSynced);
      if (i >= 0) syncListeners.splice(i, 1);
    }
  };

  if (windowListenersAttached) return teardown;
  windowListenersAttached = true;

  const trySync = async () => {
    if (!navigator.onLine || !hasPendingActions()) return;
    const result = await flushQueue();
    // Fire listeners on ANY result (not just synced>0 || dropped>0) so a
    // pure-failure flush with 0 synced and 0 dropped still gives the UI
    // a chance to react (e.g. re-render the pending-count badge).
    if (result.synced > 0 || result.dropped > 0 || result.failed > 0) {
      syncListeners.forEach((cb) => {
        try { cb(result); } catch {}
      });
    }
  };

  window.addEventListener("online", trySync);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      trySync();
    }
  });
  // Periodic retry. Previously the queue only drained on `online`,
  // `visibilitychange→visible`, and boot — so a habit log that failed with a
  // transient 5xx AFTER those events (e.g. mid-deploy while the app stays
  // foregrounded) sat until the next background/relaunch, and enough such
  // encounters could hit the drop cap without a fresh attempt in between
  // (audit 2026-08-08). A 60s tick keeps trying while the app is open;
  // trySync itself no-ops when offline or the queue is empty, so it's cheap.
  window.setInterval(() => { trySync(); }, 60_000);

  trySync();
  return teardown;
}
